import React from "react";
import SliderLayout from "../layouts/SliderLayout";
import {
    getPlanPrice,
    findPlanByHandle,
    friendlyPrice,
    getPricesFromPlan,
    getInboundAllocationFromSlider,
    getInboundSliderFromAllocation,
} from "../../utils/calculators";
import { usePlansContext } from "../../context/PlansContext";
import { useAppContext } from "../../context/AppContext";
import { INBOUND } from "../../utils/constants";
import FullPlanComparison from "./BundlePlan/fullPlanComparison";
import { updateInbound } from "../../actions/appActions";
import { InputNumber } from "antd";

const sliderPrecision = 100;

const InboundSlider = props => {
    const {
        state: { inbound, outbound },
        dispatch,
    } = useAppContext();
    const {
        state: { plans },
    } = usePlansContext();

    const dispatchChanges = (planHandle, allocation) => {
        dispatch(
            updateInbound({
                planHandle: planHandle,
                allocation: allocation,
            })
        );
    }
    
    if (!(plans && inbound.planHandle)) return null; //don't render if either plans and inboundIndex are absent

    const minInboundDomains = 1000;
    const minInboundOutboundDomains = 5;
    const inboundPlan = findPlanByHandle(inbound.planHandle, plans[INBOUND]);
    let allocation = inbound.allocation;
    if(!outbound.planHandle && allocation < minInboundDomains){
        allocation = minInboundDomains
        dispatchChanges(inbound.planHandle, allocation);
    }
    const price = getPlanPrice(inboundPlan, allocation, outbound.planHandle);
    const prices = getPricesFromPlan(inboundPlan);
    const priceMin = prices[0].lowerBound;
    const priceMax = prices[prices.length - 1].upperBound;
    const sliderValue = getInboundSliderFromAllocation(allocation, prices, minInboundDomains, outbound.planHandle);

    const handleSliderChange = value => {
        /*
        if a customer signs up for outbound, they get five inbound domains 
        included in the plan

        if they want more than 5 domains, or they are signing up for inbound 
        only, the minimum number of domains they can sign up for is 1000
        */
        const valPercent = value / sliderPrecision;
        let newAllocation = getInboundAllocationFromSlider(valPercent, prices, minInboundDomains, outbound.planHandle);
        if(outbound.planHandle){
            if(newAllocation <= minInboundOutboundDomains){
                newAllocation = minInboundOutboundDomains;
            }else if(newAllocation < minInboundDomains){
                newAllocation = minInboundDomains;
            }
        }else{
            if(newAllocation < minInboundDomains){
                newAllocation = minInboundDomains;
            }
        }
        dispatchChanges(inbound.planHandle, newAllocation);
    };

    const handleNumberChange = value => {
        if(outbound.planHandle){
            if(value <= minInboundOutboundDomains){
                value = minInboundOutboundDomains;
            }else if(value <= minInboundDomains) {
                value = minInboundDomains;
            }
        }else{
            if(value < minInboundDomains){
                value = minInboundDomains;
            }
        }
        dispatchChanges(inbound.planHandle, value);
    };

    const pricingInformation = (
        <span>
            Pricing for Inbound Filtering is based on the number of domains. The more domains you
            have filtered with us, the lower the cost per domain.
            <br />
            <br />
            For custom pricing,
            <a
                href={"https://info.mailchannels.com/contact-sales"}
                rel="noopener noreferrer"
                target="_blank"
            >
                {" "}
                contact us{" "}
            </a>
            to request a quote. Let us know how many domains you support, and a MailChannels
            representative will get back to you with pricing information.
            <br />
            <br />
            The amount of email you may process for a single domain or basket of domains is subject
            to “fair use”. MailChannels does not set specific limits, but reserves the right to
            terminate service to customers who process more than a reasonable amount of email in
            their account.
        </span>
    );

    return (
        <div style={{ margin: "4rem 0px 2rem" }} data-test-id="inbound-slider-handle">
            <SliderLayout
                title={"How many domains do you want to manage for Inbound Filtering?"}
                leftLabel={
                    <span>
                        <InputNumber
                            size="large"
                            min={priceMin}
                            max={priceMax}
                            value={allocation}
                            onChange={handleNumberChange}
                        />
                        <strong
                            style={{ fontSize: "1.25rem", marginLeft: "0.5rem" }}
                        >{`domains`}</strong>
                    </span>
                }
                SliderMaxMessage={
                    <span>
                        <b>Have more domains?</b>{" "}
                        <a
                            href="https://info.mailchannels.com/contact-sales"
                            rel="noopener noreferrer"
                        >
                            Contact us{" "}
                        </a>
                        to talk with our sales team about setting up custom pricing to fit your
                        needs.
                    </span>
                }
                rightLabel={
                    <span>
                        <strong style={{ fontSize: "1.25rem" }}>{`${friendlyPrice(
                            price
                        )}/mo`}</strong>
                    </span>
                }
                value={sliderValue * sliderPrecision}
                max={sliderPrecision}
                onChange={value => handleSliderChange(value)}
            />
            <FullPlanComparison
                buttonText={`How does pricing for Inbound Filtering work?`}
                title={`Inbound Filtering Pricing`}
            >
                {pricingInformation}
            </FullPlanComparison>
        </div>
    );
};

export default InboundSlider;
